import type React from "react";
import { useContext, useEffect, useState } from "react";

import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useGetPatientGoalsQuery } from "api/hooks/useGetPatientGoalsQuery";
import { useGetPatientHealthJournalQuery } from "api/hooks/useGetPatientHealthJournalQuery";
import type { Goal as GoalType } from "api/models/Goal";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { PatientGoal } from "shared/molecules/PatientGoal";
import { CurrentPatientContext } from "utils/contexts";
import { userIsTherapist } from "utils/profile/profileHelper";

import { NonEditGoal } from "./NonEditGoal";
import { PastGoals } from "./PastGoals";

export const isGoalUndefined = (goal: Partial<GoalType> | undefined): boolean =>
  goal === undefined || Object.values(goal).every(value => value === undefined || value === null);

interface Props {
  showPastGoals?: boolean;
}

export const Goal: React.FC<Props> = ({ showPastGoals = true }) => {
  const { t } = useTranslation();
  const { patientId } = useParams<Record<string, string>>();
  const { patient, previousTreatmentId } = useContext(CurrentPatientContext);
  const notSet = t("dashboard.tables.calls.goal.not_set");
  const { profile } = useProfileContext();
  const isTherapist = userIsTherapist(profile);
  const [editGoal, setEditGoal] = useState(false);
  const [success, setSuccess] = useState(false);
  const [goal, setGoal] = useState<Partial<GoalType>>();
  const { data: healthJournal } = useGetPatientHealthJournalQuery({
    patientId: patient.id,
    treatment_id: previousTreatmentId,
  });
  const ailment = healthJournal?.ailment;

  const getPatientGoalsQuery = useGetPatientGoalsQuery({ patientId: patient.id, treatment_id: previousTreatmentId });

  useEffect(() => {
    if (getPatientGoalsQuery.data?.[0]) {
      setGoal({
        id: getPatientGoalsQuery.data[0].id,
        free_text: getPatientGoalsQuery.data[0].free_text,
        pain: getPatientGoalsQuery.data[0].pain,
        functionality: getPatientGoalsQuery.data[0].functionality,
        feeling: getPatientGoalsQuery.data[0].feeling,
      });
    }
  }, [getPatientGoalsQuery.data]);

  const successFunc = ({ id, free_text, pain, functionality, feeling }: Partial<GoalType>) => {
    setSuccess(true);
    setGoal({ id, free_text, pain, functionality, feeling });
    setEditGoal(false);
    setTimeout(() => {
      setSuccess(false);
    }, 3000);
  };

  const goalUndefined = isGoalUndefined(goal);

  const isPhysicalFunctionAvailable = () => {
    const mostPainfulLocation = patient.most_painful_location?.location;
    switch (mostPainfulLocation) {
      case "hand":
      case "shoulder":
      case "neck":
        return false;
      default:
        return true;
    }
  };

  // should really be healthJournal?.ailment === "joint_pain" but lets use location to be backwards compatible
  const isPainAvailable = () => patient.most_painful_location?.location;

  const isFeelingAvailable = () => ailment === "osteoporosis" || ailment === "fall_prevention";

  return (
    <>
      <CardContainer data-testid="goal">
        <CardTitle>
          <Typography variant="h5">{t("patients.your_goal")}</Typography>
          {editGoal || !isTherapist ? null : (
            <BaseButton
              dataTestID="add-new-goal-button"
              variant="action"
              icon="pen-to-square"
              text={goalUndefined ? t("buttons.add_new_goal") : t("buttons.edit_goal")}
              onClick={() => setEditGoal(true)}
            />
          )}
        </CardTitle>
        <CardBody>
          {editGoal ? (
            <div data-testid="edit-goal-field">
              <PatientGoal
                id={patient?.id}
                onCancel={() => setEditGoal(false)}
                onSuccess={successFunc}
                currentGoal={{
                  id: goal?.id,
                  description: goal?.free_text,
                  functionality: goal?.functionality?.toString(),
                  pain: goal?.pain?.toString(),
                  feeling: goal?.feeling?.toString(),
                }}
                isPhysicalFunctionAvailable={isPhysicalFunctionAvailable}
                isFeelingAvailable={isFeelingAvailable}
                isPainAvailable={isPainAvailable}
              />
            </div>
          ) : (
            <NonEditGoal
              canDeleteGoal={isTherapist && !goalUndefined}
              success={success}
              setGoal={setGoal}
              patient={patient}
              patientId={patientId ?? ""}
              goal={goal}
              notSet={notSet}
              isPhysicalFunctionAvailable={isPhysicalFunctionAvailable}
              isFeelingAvailable={isFeelingAvailable}
              isPainAvailable={isPainAvailable}
              error={getPatientGoalsQuery.error}
            />
          )}
        </CardBody>
        {showPastGoals && <PastGoals data={getPatientGoalsQuery.data} />}
      </CardContainer>
    </>
  );
};

const CardContainer = styled.div`
  box-sizing: border-box;
  max-width: 620px;
  margin: 0 auto;
  padding: 0 16px;
`;

export const CardTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-top: 24px;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
`;
